import { useEffect, useState, useRef } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Trophy, Target, Zap, Star, Sparkles } from 'lucide-react';
import { Milestone, Goal } from '@/hooks/useGoals';
import confetti from 'canvas-confetti';

interface MilestoneCelebrationProps {
  milestone: Milestone | null;
  goal: Goal | null;
  isOpen: boolean;
  onClose: () => void;
}

export const MilestoneCelebration = ({ milestone, goal, isOpen, onClose }: MilestoneCelebrationProps) => {
  const [showConfetti, setShowConfetti] = useState(false);
  const animationFrameRef = useRef<number | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const formatTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    const secs = Math.round((minutes % 1) * 60);
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    // Cleanup function to stop confetti when dialog closes or component unmounts
    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      // Reset confetti canvas
      confetti.reset();
    };
  }, []);

  useEffect(() => {
    // Reset confetti when dialog closes
    if (!isOpen) {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      confetti.reset();
      setShowConfetti(false);
      return;
    }

    if (isOpen && milestone && milestone.milestone_type === 'goal_reached') {
      setShowConfetti(true);
      // Trigger confetti animation
      const duration = 3000;
      const animationEnd = Date.now() + duration;

      const frame = () => {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          confetti.reset();
          setShowConfetti(false);
          return;
        }

        confetti({
          particleCount: 3,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors: ['#f5b52a', '#FF6B6B', '#1FD4D4', '#45B7D1', '#96CEB4', '#FFEAA7']
        });
        confetti({
          particleCount: 3,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors: ['#f5b52a', '#FF6B6B', '#1FD4D4', '#45B7D1', '#96CEB4', '#FFEAA7']
        });

        animationFrameRef.current = requestAnimationFrame(frame);
      };

      animationFrameRef.current = requestAnimationFrame(frame);

      // Stop confetti after 3 seconds
      timeoutRef.current = setTimeout(() => {
        confetti.reset();
        setShowConfetti(false);
        if (animationFrameRef.current !== null) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
      }, duration);
    }
  }, [isOpen, milestone]);

  const getMilestoneIcon = () => {
    if (!milestone) return <Trophy className="h-16 w-16 text-gold" />;
    
    switch (milestone.milestone_type) {
      case 'goal_reached':
        return <Trophy className="h-16 w-16 text-gold" />;
      case 'percentage_milestone':
        return <Target className="h-16 w-16 text-primary" />;
      case 'personal_best':
        return <Zap className="h-16 w-16 text-gold" />;
      default:
        return <Star className="h-16 w-16 text-gold" />;
    }
  };

  const getMilestoneTitle = () => {
    if (!milestone || !goal) return 'Congratulations!';
    
    switch (milestone.milestone_type) {
      case 'goal_reached':
        return `Goal Achieved!`;
      case 'percentage_milestone':
        return `${milestone.percentage_achieved}% Complete!`;
      case 'personal_best':
        return `New Personal Best!`;
      default:
        return 'Milestone Reached!';
    }
  };

  const getMilestoneMessage = () => {
    if (!milestone || !goal) return '';
    
    const exerciseName = goal.exercise_name || 'your goal';
    const value = milestone.achievement_value;
    const unit = milestone.achievement_unit || goal.target_unit;
    
    const isRunning = goal.goal_type === 'running';
    const targetTime = isRunning ? parseFloat(goal.description || '0') : goal.target_value;
    
    const formatValue = (val: number) => isRunning ? formatTime(val) : val;
    const formatTarget = (val: number) => isRunning ? formatTime(val) : val;
    const displayUnit = isRunning ? '' : unit;
    const distanceInfo = isRunning ? ` (${goal.target_value}km)` : '';
    
    switch (milestone.milestone_type) {
      case 'goal_reached':
        return `You've successfully reached your ${exerciseName} goal of ${formatTarget(targetTime)} ${displayUnit}${distanceInfo}! This is a huge achievement that shows your dedication and hard work. Your coach is incredibly proud of your progress!`;
      case 'percentage_milestone':
        const percentage = milestone.percentage_achieved;
        return `You're now ${percentage}% of the way to your ${exerciseName} goal! You've reached ${formatValue(value)} ${displayUnit}, making excellent progress toward ${formatTarget(targetTime)} ${displayUnit}${distanceInfo}. Keep up the momentum!`;
      case 'personal_best':
        return `You've set a new personal record in ${exerciseName}! Reaching ${formatValue(value)} ${displayUnit} is a fantastic achievement. Every personal best gets you closer to your ultimate goal of ${formatTarget(targetTime)} ${displayUnit}${distanceInfo}.`;
      default:
        return `Great job reaching this milestone in ${exerciseName}! You're making consistent progress toward your goal.`;
    }
  };

  const getArnoldMotivation = () => {
    if (!milestone) return '';
    
    const motivationalQuotes = [
      "Remember, the worst thing you can be is average. You're already proving you're anything but!",
      "Success is walking from failure to failure with no loss of enthusiasm. Keep pushing forward!", 
      "The resistance that you fight physically in the gym and the resistance that you fight in life can only build a strong character.",
      "You can't climb the ladder of success with your hands in your pockets. Your dedication is paying off!",
      "For me life is continuously being hungry. The meaning of life is not simply to exist, to survive, but to move ahead, to go up, to achieve, to conquer.",
      "The mind is the limit. As long as the mind can envision the fact that you can do something, you can do it, as long as you really believe 100 percent.",
      "What we face may look insurmountable. But I learned something from all those years of training and competing. I learned something from all those sets and reps when I didn't think I could lift another ounce of weight. What I learned is that we are always stronger than we know."
    ];
    
    return motivationalQuotes[Math.floor(Math.random() * motivationalQuotes.length)];
  };

  if (!milestone || !goal) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogTitle className="sr-only">Milestone Achievement</DialogTitle>
        <div className="text-center space-y-6 py-6">
          {/* Animated Icon */}
          <div className="relative mx-auto">
            <div className="animate-bounce">
              {getMilestoneIcon()}
            </div>
            <div className="absolute -top-2 -right-2 animate-spin">
              <Sparkles className="h-6 w-6 text-yellow-400" />
            </div>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-primary animate-pulse">
              {getMilestoneTitle()}
            </h2>
            <div className="text-lg font-semibold">
              {goal.exercise_name}: {goal.goal_type === 'running' 
                ? formatTime(milestone.achievement_value)
                : `${milestone.achievement_value} ${milestone.achievement_unit}`
              }
            </div>
          </div>

          {/* Message */}
          <div className="space-y-4 text-sm text-muted-foreground">
            <p className="leading-relaxed">
              {getMilestoneMessage()}
            </p>
            
            {/* Coach Quote */}
            <div className="bg-gradient-primary/10 p-4 rounded-lg border-l-4 border-primary">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 bg-gradient-primary rounded-full flex items-center justify-center">
                  <span className="text-black text-xs font-bold">✦</span>
                </div>
                <span className="text-sm font-semibold text-primary">Coach says:</span>
              </div>
              <p className="text-sm italic leading-relaxed">
                "{getArnoldMotivation()}"
              </p>
            </div>
          </div>

          {/* Progress Bar for percentage milestones */}
          {milestone.milestone_type === 'percentage_milestone' && (
            <div className="space-y-2">
              <div className="w-full bg-muted rounded-full h-3">
                <div 
                  className="bg-gradient-primary h-3 rounded-full transition-all duration-1000 ease-out"
                  style={{ width: `${milestone.percentage_achieved}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {milestone.percentage_achieved}% toward your goal of {goal.goal_type === 'running' 
                  ? `${formatTime(parseFloat(goal.description || '0'))} (${goal.target_value}km)`
                  : `${goal.target_value} ${goal.target_unit}`
                }
              </p>
            </div>
          )}

          {/* Action Button */}
          <Button 
            onClick={onClose} 
            className="w-full bg-gradient-primary text-black hover:shadow-lg transition-all duration-200"
          >
            Continue Training
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};